import { create } from 'zustand';
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware';

import { createArticleBatchSlice } from 'app/modules/wallforming/components/state-management/placement/slices/articles/articleBatchSlice';
import { createArticleEventHandlerSlice } from 'app/modules/wallforming/components/state-management/placement/slices/articles/articleEventHandlerSlice';
import { createArticleGroupSlice } from 'app/modules/wallforming/components/state-management/placement/slices/articles/articleGroupSlice';
import { createArticleSlice } from 'app/modules/wallforming/components/state-management/placement/slices/articles/articleSlice';
import { createArticleTransformationSlice } from 'app/modules/wallforming/components/state-management/placement/slices/articles/articleTransformationSlice';
import { createCameraSlice } from 'app/modules/wallforming/components/state-management/placement/slices/cameraSlice';
import { createColliderSlice } from 'app/modules/wallforming/components/state-management/placement/slices/colliderSlice';
import { createConnectionSlice } from 'app/modules/wallforming/components/state-management/placement/slices/connectionSlice';
import { createDevToolsSlice } from 'app/modules/wallforming/components/state-management/placement/slices/devToolsSlice';
import { createHelperSlice } from 'app/modules/wallforming/components/state-management/placement/slices/helperSlice';
import { createPoiSlice } from 'app/modules/wallforming/components/state-management/placement/slices/poiSlice';
import { createProjectsSlice } from 'app/modules/wallforming/components/state-management/placement/slices/projectsSlice';
import { createSceneEventHandlerSlice } from 'app/modules/wallforming/components/state-management/placement/slices/scenes/sceneEventHandlerSlice';
import { createSceneSlice } from 'app/modules/wallforming/components/state-management/placement/slices/scenes/sceneSlice';
import { createUiSlice } from 'app/modules/wallforming/components/state-management/placement/slices/uiSlice';

import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

type PlacementPersistedState = Pick<
    PlacementState,
    | 'selectedSettingType'
    | 'settingTypeFilter'
    | 'selectedSettingView'
    | 'selectedArticleId'
    | 'poiSelection'
    | 'interactionMode'
    | 'disableCameraAlignBySource'
>;

const placementPersistStorageKey = 'wallforming-placement-state';

const placementPersistStorage = createJSONStorage<PlacementPersistedState>(() => sessionStorage);

export const usePlacementStore = create<PlacementState>()(
    persist(
        subscribeWithSelector((...args) => ({
            ...createUiSlice(...args),
            ...createPoiSlice(...args),
            ...createArticleSlice(...args),
            ...createArticleTransformationSlice(...args),
            ...createArticleEventHandlerSlice(...args),
            ...createArticleBatchSlice(...args),
            ...createArticleGroupSlice(...args),
            ...createProjectsSlice(...args),
            ...createSceneSlice(...args),
            ...createHelperSlice(...args),
            ...createCameraSlice(...args),
            ...createConnectionSlice(...args),
            ...createSceneEventHandlerSlice(...args),
            ...createColliderSlice(...args),
            ...createDevToolsSlice(...args)
        })),
        {
            name: placementPersistStorageKey,
            storage: placementPersistStorage,
            partialize: (state): PlacementPersistedState => ({
                settingTypeFilter: state.settingTypeFilter,
                selectedSettingType: state.selectedSettingType,
                selectedSettingView: state.selectedSettingView,
                selectedArticleId: state.selectedArticleId,
                poiSelection: state.poiSelection,
                interactionMode: state.interactionMode,
                disableCameraAlignBySource: state.disableCameraAlignBySource
            })
        }
    )
);
